import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LocalStorageService } from 'ngx-webstorage';
import * as lodash from 'lodash';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, first, mergeMap, switchMap, tap } from 'rxjs/operators';

import { ORGANIZATION_ADMIN, PROJECT_ADMIN, PROJECT_MONITOR } from 'app/types/authority';



export enum RequestStatus {
  IN_PROGRESS = 'in_progress',
  FINISHED = 'finished',
}

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  readonly account$: Observable<App.Account | null>;

  private initRequestStatus$ = new BehaviorSubject<null | RequestStatus>(null);
  private _account$ = new BehaviorSubject<App.Account | null>(null);
  private _selectedOrganization$ = new BehaviorSubject<App.Organization>(null);
  private _selectedProject$ = new BehaviorSubject<App.Project>(null);

  constructor(private http: HttpClient, private localStorage: LocalStorageService) {
    this.account$ = this._account$.asObservable().pipe(
      // do not emit multiple duplicate values
      distinctUntilChanged(lodash.isEqual),
    );
    this.reset().subscribe();
  }

  /**
   * Update authentication state. If new authentication state is null, the user is from the
   * frontend perspective logged out.
   */
  authenticate(identity?: App.Account): void {
    this._account$.next(identity ? identity : null);
  }

  /**
   * Whether user has any of the required authorities.
   * @param account account to check.
   * @param authorities authorities to check. If empty, this method always returns true.
   */
  accountHasAnyAuthority(account: App.Account, authorities: Array<string | App.Organization | App.Project> | null): boolean {
    if (!authorities || authorities.length === 0) {
      return true;
    }
    if (!account || !account.roles) {
      return false;
    }

    const authoritySet: Set<string> = new Set(
      lodash.flatMap(
        authorities.map((item) => {
          if (lodash.isObject(item)) {
            if ((item as App.Organization).organizationId) {
              return [`${ORGANIZATION_ADMIN}:${item.id}`];
            }
            if ((item as App.Project).projectId) {
              return [`${PROJECT_ADMIN}:${item.id}`, `${PROJECT_MONITOR}:${item.id}`];
            }
          }
          return [item as string];
        })
      )
    );

    return account.roles.some((roleItem) => {
      return authoritySet.has(roleItem.authorityName!)
        || (!lodash.isNil(roleItem.organizationId) && authoritySet.has(`${roleItem.authorityName}:${roleItem.organizationId}`))
        || (!lodash.isNil(roleItem.projectId) && authoritySet.has(`${roleItem.authorityName}:${roleItem.projectId}`));
    });
  }

  /**
   * Reset authentication state based on the current authentication state in the server.
   */
  reset(): Observable<App.Account | null> {
    if (this.initRequestStatus$.value === RequestStatus.IN_PROGRESS) {
      return of(null)
        .pipe(
          mergeMap(() => {
            return this.initRequestStatus$.pipe(
              filter((status) => {
                return status === RequestStatus.FINISHED;
              })
            );
          }),
          switchMap(() => {
            return this._account$.pipe(first());
          })
        );
    } else {
      this.initRequestStatus$.next(RequestStatus.IN_PROGRESS);
      return this.get()
        .pipe(
          catchError(() => of(null)),
          tap((account): void => {
            this._account$.next(account ? account : null)
          }),
          finalize((): void => {
            this.initRequestStatus$.next(RequestStatus.FINISHED);
          })
        );
    }
  }

  /**
   * Returns the single latest identity. If the user is not logged in, login status will be
   * checked with the server.
   */
  identity(): Observable<App.Account | null> {
    // check and see if we have retrieved the userIdentity data from the server.
    // if we have, reuse it by immediately resolving
    return this._account$.pipe(
      first(),
      mergeMap((user?: App.Account) => {
        if (this.initRequestStatus$.value === RequestStatus.FINISHED) {
          return of(user);
        }
        return user ? of(user) : this.reset();
      }),
    );
  }

  getSelectedOrganization(): Observable<App.Organization> {
    return this._selectedOrganization$.asObservable();
  }

  setSelectedOrganization(value: App.Organization): void {
    if (value) {
      let selectedPorject = this._selectedProject$.value;

      this.localStorage.store('currOrg', {
        id: value.id,
        name: value.name,
        organizationId: value.organizationId
      });
      if (selectedPorject && value.projects) {
        selectedPorject = value.projects.find((projectItem) => {
          return projectItem.id === selectedPorject.id;
        });
      }
      if (!selectedPorject && value.projects) {
        selectedPorject = value.projects[0];
        this.setSelectedProject(selectedPorject);
      }
    }
    return this._selectedOrganization$.next(value);
  }

  getSelectedProject(): Observable<App.Project> {
    return this._selectedProject$.asObservable();
  }

  setSelectedProject(value: App.Project): void {
    if (value) {
      this.localStorage.store('currProject', {
        id: value.id,
        name: value.projectName,
        projectId: value.projectId,
        projectName: value.projectName,
        alias: value.alias,
        softwareVersion: value.softwareVersion
      });
    }
    return this._selectedProject$.next(value);
  }

  get(): Observable<App.Account> {
    return this.http.get<App.Account>('api/account');
  }

  save(account: App.Account): Observable<App.Account> {
    return this.http.post<App.Account>('api/account', account);
  }
}
